import { Box, Divider, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconLayout, IconLayoutDistributeHorizontal, IconContainer } from '@tabler/icons-react';
import { usePreferencesStore } from '../../../stores';
import SegmentedControl from '../../../components/ui/navigation/SegmentedControl';

export default function LayoutPreferencesSection() {
  const layoutWidth = usePreferencesStore((state) => state.layoutWidth);
  const setLayoutWidth = usePreferencesStore((state) => state.setLayoutWidth);
  const layoutPadding = usePreferencesStore((state) => state.layoutPadding);
  const setLayoutPadding = usePreferencesStore((state) => state.setLayoutPadding);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group gap="sm">
        <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
          <IconLayout size={20} />
        </ThemeIcon>
        <Box>
          <Text fw={600}>Layout Preferences</Text>
          <Text size="xs" c="dimmed">
            Customize page layout and content width
          </Text>
        </Box>
      </Group>

      <Divider />

      {/* Layout Width Control */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Content Width
        </Text>
        <Text size="xs" c="dimmed" mb="xs">
          Choose how wide you want the content to display
        </Text>
        <SegmentedControl
          fullWidth
          activeGradient
          data={[
            {
              label: (
                <Group gap="xs" justify="center">
                  <IconContainer size={16} />
                  <span>Boxed</span>
                </Group>
              ),
              value: 'boxed',
            },
            {
              label: (
                <Group gap="xs" justify="center">
                  <IconLayoutDistributeHorizontal size={16} />
                  <span>Full Width</span>
                </Group>
              ),
              value: 'full-width',
            },
          ]}
          value={layoutWidth}
          onChange={(value) => setLayoutWidth(value as typeof layoutWidth)}
        />
        <Text size="xs" c="dimmed" mt="xs">
          {layoutWidth === 'boxed'
            ? 'Content is contained to 1320px for better readability'
            : 'Content stretches to fill the available screen width'}
        </Text>
      </Box>

      {/* Padding Control - Only visible when full-width is selected */}
      {layoutWidth === 'full-width' && (
        <Box>
          <Text size="sm" fw={500} mb="xs">
            Horizontal Padding
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            Adjust spacing on the left and right edges
          </Text>
          <SegmentedControl
            fullWidth
            activeGradient
            data={[
              { label: 'XS', value: 'xs' },
              { label: 'SM', value: 'sm' },
              { label: 'MD', value: 'md' },
              { label: 'LG', value: 'lg' },
              { label: 'XL', value: 'xl' },
            ]}
            value={layoutPadding}
            onChange={(value) => setLayoutPadding(value as typeof layoutPadding)}
          />
          <Text size="xs" c="dimmed" mt="xs">
            {layoutPadding === 'xs' && 'Minimal spacing (12-24px responsive) - maximize content area'}
            {layoutPadding === 'sm' && 'Tight spacing (16-48px responsive) - compact layout'}
            {layoutPadding === 'md' && 'Comfortable spacing (20-72px responsive) - balanced layout'}
            {layoutPadding === 'lg' && 'Generous spacing (24-96px responsive) - extra breathing room'}
            {layoutPadding === 'xl' && 'Very spacious (28-120px responsive) - premium feel'}
          </Text>
        </Box>
      )}
    </Stack>
  );
}
